<script setup lang="ts">
/**
 * Single-purpose icon component used by `sbi.viz.table` action buttons
 * (and any future surface that takes a kebab-case icon name and needs
 * to render the matching glyph).
 *
 * The author surface speaks kebab-case strings (`map-pin`, `chart-bar`,
 * `triangle-alert`). The underlying icon library exports PascalCase
 * Vue components. This component owns the indirection so the rest of
 * the codebase never imports icons directly — extending the supported
 * set is a one-file edit to `shared/lucide-icons.ts` plus one line
 * added in two places here (the named import and the lookup map).
 *
 * **Insights are read-only.** The set of supported icons here mirrors
 * the curated 200 listed in `shared/lucide-icons.ts`; the briefing
 * topic ('icons') and the `lucent_icon_search` MCP tool both index
 * against the same list. Icons whose default semantic prior implies
 * mutation (delete, edit, send, export) are deliberately absent.
 *
 * The library is intentionally not named in author-facing docs — we
 * may swap glyph sets later.
 */
import { computed } from "vue";
import {
  Accessibility,
  Activity,
  AlarmClock,
  Anchor,
  Apple,
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Award,
  Baby,
  Badge,
  BadgeDollarSign,
  Banknote,
  Bed,
  Beer,
  Bell,
  Bike,
  Book,
  BookOpen,
  Bookmark,
  Box,
  Brain,
  Building,
  Building2,
  Bus,
  Cake,
  Calculator,
  Calendar,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Camera,
  Car,
  Castle,
  ChartArea,
  ChartBar,
  ChartCandlestick,
  ChartColumn,
  ChartLine,
  ChartPie,
  ChartScatter,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Church,
  Circle,
  CircleCheck,
  CircleHelp,
  CircleX,
  Clipboard,
  ClipboardList,
  Clock,
  Cloud,
  CloudRain,
  Coffee,
  Coins,
  Columns3,
  Computer,
  Contact,
  CornerUpRight,
  CreditCard,
  Crown,
  CupSoda,
  Database,
  Diamond,
  DollarSign,
  Droplet,
  Dumbbell,
  Ear,
  Ellipsis,
  EllipsisVertical,
  Euro,
  ExternalLink,
  Eye,
  Factory,
  Fence,
  FileText,
  Filter,
  Flag,
  Flame,
  Flower,
  Folder,
  FolderOpen,
  Footprints,
  Frown,
  Fuel,
  Gauge,
  Gem,
  Gift,
  Glasses,
  Globe,
  Goal,
  GraduationCap,
  Grid3x3,
  HandCoins,
  Hand,
  Hash,
  Headphones,
  Heart,
  HeartPulse,
  Hexagon,
  History,
  Hospital,
  Hotel,
  Hourglass,
  House,
  Info,
  Key,
  Keyboard,
  Landmark,
  Laptop,
  Layers,
  Leaf,
  Library,
  Lightbulb,
  Link as LinkIcon,
  List,
  Lock,
  MapPin,
  Martini,
  Medal,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Monitor,
  Moon,
  Mountain,
  Move,
  Navigation,
  Notebook,
  OctagonAlert,
  OctagonX,
  Package,
  Paperclip,
  Percent,
  PersonStanding,
  Phone,
  PiggyBank,
  Pin,
  Pizza,
  Plane,
  PoundSterling,
  Receipt,
  Route,
  Salad,
  Sandwich,
  Scale,
  School,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  Ship,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Snowflake,
  Soup,
  Sparkles,
  Square,
  SquareParking,
  Star,
  Stethoscope,
  Store,
  Sun,
  Sunrise,
  Sunset,
  Table,
  Tag,
  Tags,
  Target,
  Tent,
  Thermometer,
  Ticket,
  Timer,
  TrainFront,
  TreeDeciduous,
  TreePine,
  Trees,
  TrendingDown,
  TrendingUp,
  Triangle,
  TriangleAlert,
  Trophy,
  Truck,
  User,
  Users,
  Utensils,
  Wallet,
  Warehouse,
  Watch,
  Waves,
  Wine,
  Wrench,
  X,
  Zap,
} from "lucide-vue-next";

import type { LucideIconName } from "../../../shared/lucide-icons.ts";

const props = withDefaults(
  defineProps<{
    /**
     * Icon name in kebab-case. Validated server-side at the worker
     * boundary; unknown names fall back to the `info` glyph here so
     * the table still renders rather than crashing on a typo that
     * slips past validation (defence in depth — never expected to
     * fire in practice).
     */
    name: LucideIconName | string;
    /** Pixel size of the rendered SVG. Defaults to 16 (desktop). */
    size?: number;
    /** Stroke weight. 2 matches the rest of the embed surface. */
    strokeWidth?: number;
  }>(),
  {
    size: 16,
    strokeWidth: 2,
  },
);

// kebab-case author-facing name → PascalCase Vue component from the
// underlying icon library. Mirrors the LUCIDE_ICONS enum in
// shared/lucide-icons.ts — keep the two in lock-step. The
// `lucide-icons.test.ts → "stays in sync"` check catches drift.
const ICON_COMPONENT: Record<string, unknown> = {
  // People & identity
  users: Users,
  user: User,
  contact: Contact,
  smile: Smile,
  frown: Frown,
  glasses: Glasses,
  accessibility: Accessibility,
  baby: Baby,
  "person-standing": PersonStanding,
  hand: Hand,
  footprints: Footprints,
  // Body & wellness
  brain: Brain,
  ear: Ear,
  eye: Eye,
  heart: Heart,
  "heart-pulse": HeartPulse,
  activity: Activity,
  dumbbell: Dumbbell,
  stethoscope: Stethoscope,
  thermometer: Thermometer,
  // Food & drink
  apple: Apple,
  salad: Salad,
  utensils: Utensils,
  coffee: Coffee,
  "cup-soda": CupSoda,
  martini: Martini,
  wine: Wine,
  beer: Beer,
  cake: Cake,
  pizza: Pizza,
  sandwich: Sandwich,
  soup: Soup,
  // Places & buildings
  building: Building,
  "building-2": Building2,
  house: House,
  store: Store,
  factory: Factory,
  school: School,
  hospital: Hospital,
  hotel: Hotel,
  warehouse: Warehouse,
  landmark: Landmark,
  castle: Castle,
  church: Church,
  tent: Tent,
  bed: Bed,
  // Nature & geography
  trees: Trees,
  "tree-deciduous": TreeDeciduous,
  "tree-pine": TreePine,
  mountain: Mountain,
  waves: Waves,
  droplet: Droplet,
  flame: Flame,
  flower: Flower,
  leaf: Leaf,
  globe: Globe,
  fence: Fence,
  // Travel & transport
  car: Car,
  truck: Truck,
  bike: Bike,
  bus: Bus,
  "train-front": TrainFront,
  plane: Plane,
  ship: Ship,
  anchor: Anchor,
  fuel: Fuel,
  "square-parking": SquareParking,
  navigation: Navigation,
  route: Route,
  "map-pin": MapPin,
  // Time & dates
  calendar: Calendar,
  "calendar-days": CalendarDays,
  "calendar-clock": CalendarClock,
  "calendar-range": CalendarRange,
  clock: Clock,
  "alarm-clock": AlarmClock,
  timer: Timer,
  hourglass: Hourglass,
  history: History,
  watch: Watch,
  // Weather
  sun: Sun,
  moon: Moon,
  sunrise: Sunrise,
  sunset: Sunset,
  cloud: Cloud,
  "cloud-rain": CloudRain,
  snowflake: Snowflake,
  // Money & finance
  "dollar-sign": DollarSign,
  euro: Euro,
  "pound-sterling": PoundSterling,
  banknote: Banknote,
  coins: Coins,
  "credit-card": CreditCard,
  wallet: Wallet,
  "piggy-bank": PiggyBank,
  receipt: Receipt,
  percent: Percent,
  calculator: Calculator,
  scale: Scale,
  "hand-coins": HandCoins,
  "badge-dollar-sign": BadgeDollarSign,
  // Charts & analytics
  "chart-bar": ChartBar,
  "chart-line": ChartLine,
  "chart-pie": ChartPie,
  "chart-area": ChartArea,
  "chart-column": ChartColumn,
  "chart-scatter": ChartScatter,
  "chart-candlestick": ChartCandlestick,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  gauge: Gauge,
  // Documents & data
  "file-text": FileText,
  folder: Folder,
  "folder-open": FolderOpen,
  paperclip: Paperclip,
  clipboard: Clipboard,
  "clipboard-list": ClipboardList,
  book: Book,
  "book-open": BookOpen,
  library: Library,
  archive: Archive,
  layers: Layers,
  database: Database,
  // Communication
  phone: Phone,
  "message-square": MessageSquare,
  "message-circle": MessageCircle,
  bell: Bell,
  megaphone: Megaphone,
  // Devices & tech
  computer: Computer,
  laptop: Laptop,
  smartphone: Smartphone,
  monitor: Monitor,
  keyboard: Keyboard,
  headphones: Headphones,
  camera: Camera,
  // Arrows & directions
  "arrow-right": ArrowRight,
  "arrow-left": ArrowLeft,
  "arrow-up": ArrowUp,
  "arrow-down": ArrowDown,
  "chevron-right": ChevronRight,
  "chevron-left": ChevronLeft,
  "chevron-up": ChevronUp,
  "chevron-down": ChevronDown,
  "corner-up-right": CornerUpRight,
  move: Move,
  // Links & overflow
  "external-link": ExternalLink,
  link: LinkIcon,
  ellipsis: Ellipsis,
  "ellipsis-vertical": EllipsisVertical,
  // Status signals
  info: Info,
  "triangle-alert": TriangleAlert,
  "circle-check": CircleCheck,
  "circle-help": CircleHelp,
  "circle-x": CircleX,
  "octagon-alert": OctagonAlert,
  "octagon-x": OctagonX,
  shield: Shield,
  "shield-check": ShieldCheck,
  lock: Lock,
  check: Check,
  x: X,
  // Tags & labels
  tag: Tag,
  tags: Tags,
  hash: Hash,
  bookmark: Bookmark,
  flag: Flag,
  pin: Pin,
  star: Star,
  badge: Badge,
  // UI controls
  filter: Filter,
  search: Search,
  settings: Settings,
  "sliders-horizontal": SlidersHorizontal,
  list: List,
  table: Table,
  "columns-3": Columns3,
  "grid-3x3": Grid3x3,
  // Shapes & containers
  circle: Circle,
  square: Square,
  triangle: Triangle,
  hexagon: Hexagon,
  diamond: Diamond,
  box: Box,
  package: Package,
  gift: Gift,
  // Recognition & rewards
  trophy: Trophy,
  medal: Medal,
  award: Award,
  target: Target,
  goal: Goal,
  ticket: Ticket,
  crown: Crown,
  gem: Gem,
  // Misc
  sparkles: Sparkles,
  zap: Zap,
  "graduation-cap": GraduationCap,
  notebook: Notebook,
  lightbulb: Lightbulb,
  key: Key,
  wrench: Wrench,
};

const component = computed(() => ICON_COMPONENT[props.name] ?? Info);
</script>

<template>
  <component
    :is="component"
    :size="size"
    :stroke-width="strokeWidth"
    aria-hidden="true"
  />
</template>
